/**
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 **/
import React, { ReactElement, useCallback } from 'react';
import { gql, useMutation } from '@apollo/client';
import { CardSort } from '@userzoomplatform/pe-study-tasks';
import { CardSortTaskData, CardSortTaskAnswer } from '@userzoomplatform/pe-study-tasks/dist/types/task';
import { Texts } from '@userzoomplatform/pe-study-tasks/dist/texts/types';
import { useTranslation } from 'react-i18next';

import { TASKS_FIELDS } from '../fragments';
import config from '../../../utils/config';
import { Namespaces } from '../../../utils/i18n';

export interface Props {
	data: CardSortTaskData;
	systemTexts: Texts['systemTexts'];
	setTask: (task: CardSortTaskData) => void;
}

const defaultProps = {};

const answerCardsortTaskMutation = gql`
	${TASKS_FIELDS}
	mutation AnswerCardSortTask($input: AnswerCardSortTask!) {
		answerCardSortTask(input: $input) {
			...TasksFields
		}
	}
`;

const CardSortTask = ({ data, systemTexts, setTask }: Props): ReactElement => {
	const { assetsBasePath } = config();
	const { t } = useTranslation([Namespaces.Desktop]);

	const [answerCardSortTask] = useMutation(answerCardsortTaskMutation, {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		onCompleted: (response: any) => {
			if (response?.answerCardSortTask) {
				setTask(response?.answerCardSortTask);
			}
		},
		onError: (error: unknown) => {
			// eslint-disable-next-line no-console
			console.error(error);
		},
	});

	const onAnswer = useCallback(
		async (answer: CardSortTaskAnswer) => {
			answerCardSortTask({
				variables: {
					input: answer,
				},
			});
		},
		[answerCardSortTask]
	);

	const texts = {
		systemTexts,
		copies: {
			cardSort: {
				items: t(`${Namespaces.Desktop}:cardSort.items`),
				dropItemToCreateCategory: t(`${Namespaces.Desktop}:cardSort.dropItemToCreateCategory`),
			},
		},
	};

	return (
		<CardSort
			key={`card-sort-${data.taskId}`}
			data={{
				...data,
				onAnswer,
				onExit: () => window.close(),
			}}
			texts={texts}
			config={{ assetsBasePath }}
		/>
	);
};

CardSortTask.defaultProps = defaultProps;

export default CardSortTask;
